import { Redirect, router } from 'expo-router';
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import {
  cancelAccountDeletion,
  isDeletionBlocking,
} from '@/src/features/account-deletion/account-deletion-service';
import { useAuth } from '@/src/features/auth/use-auth';
import { useProfile } from '@/src/features/profile/use-profile';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';

export default function AccountDeletionPendingScreen() {
  const { signOut, user } = useAuth();
  const profileQuery = useProfile(user?.uid);
  const { showError, showSuccess } = useToast();
  const [canceling, setCanceling] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const deletion = profileQuery.data?.deletion;

  if (profileQuery.isLoading) {
    return <LoadingView message="Checking account deletion status..." />;
  }

  if (!isDeletionBlocking(deletion)) {
    return <Redirect href="/home" />;
  }

  async function handleCancelDeletion() {
    setCanceling(true);

    try {
      await cancelAccountDeletion();
      await profileQuery.refetch();
      showSuccess('Your account deletion request was canceled.', 'Restored');
      router.replace('/home');
    } catch (error) {
      showError(error, 'Unable to cancel account deletion.');
    } finally {
      setCanceling(false);
    }
  }

  async function handleSignOut() {
    setSigningOut(true);

    try {
      await signOut();
      router.replace('/sign-in');
    } catch (error) {
      showError(error, 'Unable to sign out.');
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <Screen
      title="Account deletion scheduled"
      subtitle="Your account is in the 30-day recovery window."
    >
      <View style={styles.panel}>
        <Text style={styles.heading}>Recovery window</Text>
        <Text style={styles.copy}>
          Your account is suspended in Frienzzi and is scheduled for permanent
          deletion after {formatDate(deletion?.purgeAfter)}.
        </Text>
        <Text style={styles.copy}>
          Confirmation code: {deletion?.confirmationCode || 'Not available'}
        </Text>
        <Text style={styles.copy}>
          You can cancel this request before the recovery window expires.
        </Text>
      </View>
      <Button
        title="Cancel deletion"
        loading={canceling}
        onPress={handleCancelDeletion}
      />
      <Button
        title="Sign out"
        variant="secondary"
        loading={signingOut}
        onPress={handleSignOut}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  heading: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  copy: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
});
